import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { Checkbox } from "primereact/checkbox";
import Gallery from "../components/Gallery";
import axios from "axios";

interface BlogPost {
  id: string;
  title: string;
  text: string;
  imageUrl?: string;
  galleryImages: string[];
  youtubeUrl?: string;
  createdAt: number;
  isSliding?: boolean;
  showInHome?: boolean;
}

const EditBlog: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = React.useRef<Toast>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [blog, setBlog] = useState<BlogPost>({
    id: "",
    title: "",
    text: "",
    imageUrl: "",
    galleryImages: [],
    youtubeUrl: "",
    createdAt: Date.now(),
    isSliding: false,
    showInHome: false,
  });

  useEffect(() => {
    const fetchBlog = async () => {
      if (!id) return;

      try {
        setLoading(true);
        // const blogDoc = await getDoc(doc(db, 'posts', id));
        // if (blogDoc.exists()) {
        //     setBlog({ id: blogDoc.id, ...blogDoc.data() } as BlogPost);
        // } else {
        //     console.error('Blog not found');
        //     navigate('/');
        // }
        // const response = await axios.get(
        //   process.env.REACT_APP_API + `/posts/${id}`
        // );
        const response = await axios
          .get(`${process.env.REACT_APP_API}/posts/${id}`)
          .then((response) => setBlog(response.data))
          .catch((error) => console.error("Error fetching post:", error));
      } catch (error) {
        console.error("Error fetching blog:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load blog post",
          life: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [id, navigate]);

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", "ml_default");
        formData.append("cloud_name", "dzuppkxqa");

        const response = await fetch(
          `https://api.cloudinary.com/v1_1/dzuppkxqa/image/upload`,
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await response.json();
        setBlog((prev) => ({ ...prev, imageUrl: data.secure_url }));
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to upload image",
          life: 3000,
        });
      } finally {
        setUploading(false);
      }
    }
  };

  const handleGalleryImagesChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files) {
      try {
        setUploading(true);
        const uploadPromises = Array.from(files).map(async (file) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("upload_preset", "ml_default");
          formData.append("cloud_name", "dzuppkxqa");

          const response = await fetch(
            `https://api.cloudinary.com/v1_1/dzuppkxqa/image/upload`,
            {
              method: "POST",
              body: formData,
            }
          );

          const data = await response.json();
          return data.secure_url;
        });

        const newImageUrls = await Promise.all(uploadPromises);
        setBlog((prev) => ({
          ...prev,
          galleryImages: [...prev.galleryImages, ...newImageUrls],
        }));
      } catch (error) {
        console.error("Error uploading gallery images:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to upload gallery images",
          life: 3000,
        });
      } finally {
        setUploading(false);
      }
    }
  };

  const removeGalleryImage = (index: number) => {
    setBlog((prev) => ({
      ...prev,
      galleryImages: prev.galleryImages.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      if (!blog.showInHome && blog.showInHome !== undefined) {
        // Make a request to check how many blogs are currently set to show on the homepage
        const response = await axios.get(
          process.env.REACT_APP_API + "/posts?showInHome=true"
        );

        if (response.data.length >= 3) {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Maximum of 3 blogs can be shown on home page",
            life: 3000,
          });
          setBlog((prev) => ({ ...prev, showInHome: false }));
          return;
        }
      }

      await axios.put(process.env.REACT_APP_API + `/posts/${id}`, blog);

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Blog post updated successfully",
        life: 3000,
      });
      navigate("/blog");
    } catch (error) {
      console.error("Error updating blog:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update blog post",
        life: 3000,
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <i className="pi pi-spin pi-spinner text-4xl"></i>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <Toast ref={toast} />
      <Card className="mb-4">
        <form onSubmit={handleSubmit} className="flex flex-column gap-4">
          <div className="flex flex-column gap-2">
            <label htmlFor="title">Title</label>
            <InputText
              id="title"
              value={blog.title}
              onChange={(e) =>
                setBlog((prev) => ({ ...prev, title: e.target.value }))
              }
              required
            />
          </div>

          <div className="flex flex-column gap-2">
            <label htmlFor="text">Content</label>
            <InputTextarea
              id="text"
              value={blog.text}
              onChange={(e) =>
                setBlog((prev) => ({ ...prev, text: e.target.value }))
              }
              rows={5}
              required
              className="whitespace-pre-wrap w-full"
            />
          </div>

          <div className="field w-full max-w-3xl mb-8">
            <label
              htmlFor="image"
              className="block text-lg font-medium text-gray-700 mb-2"
            >
              Main Image
            </label>
            <div className="flex flex-col items-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 transition-colors">
              <input
                type="file"
                id="image"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleImageChange}
                style={{ display: "none" }}
              />
              {blog.imageUrl ? (
                <div
                  className="relative group cursor-pointer flex flex-col items-center"
                  onClick={() => document.getElementById("image")?.click()}
                >
                  <img
                    src={blog.imageUrl}
                    alt="Preview"
                    width="250"
                    height="200"
                    style={{ display: "block" }}
                    className="rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                    <span className="text-white text-sm">Click to change</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <i className="pi pi-image text-4xl text-gray-400"></i>
                  <div className="flex flex-col items-center gap-2">
                    <Button
                      label="Choose Image"
                      icon="pi pi-upload"
                      className="p-button-rounded p-button-outlined"
                      onClick={() => document.getElementById("image")?.click()}
                    />
                    <p className="text-sm text-gray-500">
                      JPG, PNG, GIF or WebP (max. 5MB)
                    </p>
                  </div>
                </div>
              )}
              {uploading && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                  <i className="pi pi-spin pi-spinner text-2xl text-white"></i>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-column gap-2">
            <label>Gallery Images</label>
            <div className="flex align-items-center gap-2 mb-4">
              <Button
                type="button"
                icon="pi pi-images"
                className="p-button-secondary"
                onClick={() => document.getElementById("gallery")?.click()}
              />
              <input
                type="file"
                id="gallery"
                accept="image/*"
                multiple
                onChange={handleGalleryImagesChange}
                style={{ display: "none" }}
              />
            </div>
            {blog.galleryImages.length > 0 && (
              <Gallery
                images={blog.galleryImages}
                onImageRemove={removeGalleryImage}
                isEditing={true}
              />
            )}
          </div>

          <div className="field w-full max-w-3xl mb-8">
            <label
              htmlFor="youtube"
              className="block text-lg font-medium text-gray-700 mb-2"
            >
              YouTube Video Link (Optional)
            </label>
            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-youtube text-red-500"></i>
              </span>
              <InputText
                id="youtube"
                value={blog.youtubeUrl || ""}
                onChange={(e) =>
                  setBlog((prev) => ({ ...prev, youtubeUrl: e.target.value }))
                }
                placeholder="Enter YouTube video URL"
                className="w-full"
              />
            </div>
            <small className="text-gray-500">
              Paste the full YouTube video URL (e.g.,
              https://www.youtube.com/watch?v=...)
            </small>
          </div>

          <div className="field-checkbox mb-4">
            <Checkbox
              inputId="showInHome"
              checked={blog.showInHome || false}
              onChange={(e) => setBlog({ ...blog, showInHome: e.checked })}
            />
            <label htmlFor="showInHome" className="ml-2">
              Show in home page (limited to 3 posts)
            </label>
          </div>

          <div className="field">
            <Button
              type="button"
              label="Cancel"
              className="p-button-secondary"
              onClick={() => navigate("/")}
            />
            <Button type="submit" label="Save Changes" icon="pi pi-check" />
          </div>
        </form>
      </Card>
    </div>
  );
};

export default EditBlog;
